// WeatherDispatch — Roofing weather-aware dispatch board.
// Roofers can't safely work in rain, hail, storms, or high wind. This
// board scans NWS forecasts for every active job site and flags which
// need to be paused for the next 48 hours.
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CloudRain, Sun, Warning, Pause, ArrowsClockwise, Plus, X } from "@phosphor-icons/react";

const RISK_META = {
  pause:   { color: "bg-red-500 text-white",       label: "PAUSE",   icon: <CloudRain weight="fill" size={16}/> },
  watch:   { color: "bg-amber-400 text-slate-900", label: "WATCH",   icon: <Warning  weight="fill" size={16}/> },
  clear:   { color: "bg-emerald-500 text-white",   label: "CLEAR",   icon: <Sun      weight="fill" size={16}/> },
  unknown: { color: "bg-slate-400 text-white",     label: "UNKNOWN", icon: <Warning  weight="fill" size={16}/> },
};

const DEFAULT_SITES = [
  { site_id: "job-1200-sample", lat: 39.9526, lon: -75.1652, address: "1200 Sample St, Philadelphia PA" },
];

export default function WeatherDispatch() {
  const [sites, setSites] = useState(DEFAULT_SITES);
  const [results, setResults] = useState({ sites: [], counts: { pause:0, watch:0, clear:0, unknown:0 }, scanned_at: null });
  const [scanning, setScanning] = useState(false);
  const [pausing, setPausing] = useState(false);

  const loadLast = async () => {
    try {
      const r = await api.get("/roofing/weather/last-scan");
      if (r.data.scanned_at) setResults(r.data);
    } catch {}
  };
  useEffect(() => { loadLast(); }, []);

  const scan = async () => {
    if (!sites.length) { toast.error("Add at least one site"); return; }
    setScanning(true);
    try {
      const { data } = await api.post("/roofing/weather/scan", { sites });
      setResults(data);
      const paused = data.counts?.pause || 0;
      if (paused > 0) toast.error(`${paused} site${paused>1?"s":""} at risk — recommend pause`);
      else toast.success(`All ${data.sites.length} site${data.sites.length>1?"s":""} clear`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Scan failed")); }
    finally { setScanning(false); }
  };

  const pauseAll = async () => {
    const toPause = results.sites.filter(s => s.risk === "pause").map(s => s.site_id);
    if (!toPause.length) { toast.info("No sites in pause state"); return; }
    if (!window.confirm(`Auto-pause ${toPause.length} outdoor job${toPause.length>1?"s":""}? Dispatchers can un-pause anytime.`)) return;
    setPausing(true);
    try {
      const { data } = await api.post("/roofing/weather/pause-sites", {
        site_ids: toPause, note: "Weather hold — rain/hail forecast",
      });
      toast.success(`Paused ${data.modified} job${data.modified===1?"":"s"}`);
    } catch (e) { toast.error("Pause failed"); }
    finally { setPausing(false); }
  };

  const addSite = () => setSites(prev => [...prev, { site_id: `site-${prev.length+1}`, lat: 39.9526, lon: -75.1652, address: "" }]);
  const removeSite = (i) => setSites(prev => prev.filter((_, idx) => idx !== i));
  const updateSite = (i, k, v) => setSites(prev => prev.map((s, idx) => idx === i ? { ...s, [k]: v } : s));

  const pauseCount = results.counts?.pause || 0;

  return (
    <div className="p-4 md:p-6" data-testid="weather-dispatch-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#0f172a] text-white flex items-center justify-center">
            <CloudRain weight="fill" size={22} className="text-cyan-400"/>
          </div>
          <div>
            <h1 className="font-display font-black text-3xl leading-none">Weather Dispatch</h1>
            <div className="text-xs uppercase font-black tracking-widest text-slate-500 mt-1">
              48-hour NWS forecast · roofing safety
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={scan} disabled={scanning} data-testid="weather-scan-btn"
            className="h-11 px-5 bg-[#00C4CC] hover:bg-[#00a9b1] text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 disabled:opacity-50 shadow">
            <ArrowsClockwise weight="bold" size={16} className={scanning ? "animate-spin" : ""}/>
            {scanning ? "Scanning…" : "Scan Now"}
          </button>
          {pauseCount > 0 && (
            <button onClick={pauseAll} disabled={pausing} data-testid="weather-pause-all-btn"
              className="h-11 px-4 bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 disabled:opacity-50 shadow">
              <Pause weight="fill" size={14}/> Pause {pauseCount} Outdoor Job{pauseCount>1?"s":""}
            </button>
          )}
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-4 gap-2 mb-4" data-testid="weather-counts">
        {["pause","watch","clear","unknown"].map(k => {
          const m = RISK_META[k];
          return (
            <div key={k} className={`p-3 rounded-xl ${m.color}`}>
              <div className="text-2xl font-display font-black">{results.counts?.[k] ?? 0}</div>
              <div className="text-[10px] font-black uppercase tracking-widest">{m.label}</div>
            </div>
          );
        })}
      </div>

      {/* Site list */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-black uppercase tracking-widest text-xs text-slate-500">Job Sites to Scan</div>
          <button onClick={addSite} data-testid="weather-add-site"
            className="h-8 px-3 bg-slate-100 hover:bg-slate-200 text-slate-800 font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
            <Plus weight="bold" size={12}/> Add Site
          </button>
        </div>
        <div className="space-y-2">
          {sites.map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`weather-site-row-${i}`}>
              <input value={s.site_id} onChange={e => updateSite(i, "site_id", e.target.value)}
                placeholder="Job / Site ID" data-testid={`weather-site-id-${i}`}
                className="col-span-3 h-10 px-2 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg text-sm font-mono"/>
              <input type="number" step="any" value={s.lat} onChange={e => updateSite(i, "lat", parseFloat(e.target.value))}
                placeholder="Lat" data-testid={`weather-site-lat-${i}`}
                className="col-span-2 h-10 px-2 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg text-sm"/>
              <input type="number" step="any" value={s.lon} onChange={e => updateSite(i, "lon", parseFloat(e.target.value))}
                placeholder="Lon" data-testid={`weather-site-lon-${i}`}
                className="col-span-2 h-10 px-2 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg text-sm"/>
              <input list="known-addresses" autoComplete="off" value={s.address || ""} onChange={e => updateSite(i, "address", e.target.value)}
                placeholder="Address (optional)" data-testid={`weather-site-addr-${i}`}
                className="col-span-4 h-10 px-2 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg text-sm"/>
              <button onClick={() => removeSite(i)} data-testid={`weather-site-remove-${i}`}
                className="col-span-1 h-10 rounded-lg bg-slate-100 hover:bg-red-100 hover:text-red-600 text-slate-400 inline-flex items-center justify-center">
                <X size={14}/>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="weather-results">
        {results.sites.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            Hit <b>Scan Now</b> to pull the latest 48-hour NWS forecast for your job sites.
          </div>
        ) : results.sites.map((r) => {
          const m = RISK_META[r.risk] || RISK_META.unknown;
          return (
            <div key={r.site_id} data-testid={`weather-result-${r.site_id}`}
              className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
              <div className={`h-9 min-w-[74px] px-2 rounded-lg inline-flex items-center justify-center gap-1 ${m.color}`}>
                {m.icon}
                <span className="font-black text-[10px] uppercase tracking-widest">{m.label}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{r.address || r.site_id}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {r.reason} · <span className="font-mono">{r.lat?.toFixed(3)}, {r.lon?.toFixed(3)}</span>
                </div>
              </div>
              {r.worst_period && (
                <div className="hidden md:block text-right">
                  <div className="text-[10px] uppercase font-black tracking-widest text-slate-400">{r.worst_period.name}</div>
                  <div className="text-xs font-bold text-slate-700">
                    {r.worst_period.precip !== undefined && `${r.worst_period.precip}% precip · `}
                    {r.worst_period.wind || ""}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {results.scanned_at && (
        <div className="mt-3 text-[11px] text-slate-500">
          Last scanned: <span className="font-mono">{new Date(results.scanned_at).toLocaleString()}</span> · Source: National Weather Service (weather.gov)
        </div>
      )}
    </div>
  );
}
